import React from 'react';
import ListBoxContainer from './ListBoxContainer';
import ListBoxItem from './ListBoxItem';
import ListBoxSection from './ListBoxSection';
import ListBoxView from './ListBoxView';
import { ListBoxData, ListBoxProps } from './types';
import { isViewProps } from './utils';

function ForwardedListBox<T extends ListBoxData = ListBoxData>(
  props: ListBoxProps<T>,
  ref: React.ForwardedRef<HTMLUListElement>,
): React.ReactElement | null {
  if (isViewProps(props)) {
    return <ListBoxView ref={ref} {...props} />;
  }

  return <ListBoxContainer ref={ref} {...props} />;
}

/**
 * Accessible list-box component, useful when giving the user a list of options
 * to select.
 *
 * The `ListBox` needs `state` to be passed in. The `ListBox` can be used in
 * various use cases, which means that state often comes from different places.
 *
 * For example, when using the `ListBox` in a select menu, the state would come from
 * react-aria's `useSelectState`.  When using the `ListBox` as a generic list-box,
 * the state would come from `useListState`
 *
 * Defining a custom state for the ListBox can be done by passing in the `state`
 * prop.  If that prop is not provided, `useListState` from react-aria is used.
 *
 * For convenience, the `useListState` react-stately hook is exported from this
 * module as `useListBoxState`.
 */
const ListBoxComponent = React.forwardRef(ForwardedListBox) as <
  T extends ListBoxData = ListBoxData,
>(
  props: ListBoxProps<T> & { ref?: React.Ref<HTMLUListElement> },
) => React.ReactElement | null;

const ListBox = Object.assign(ListBoxComponent, {
  Item: ListBoxItem,
  Section: ListBoxSection,
});

export default ListBox;
